import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { listContacts } from '../api'
import { CrmApiError } from '../http'
import type { CrmContactListItem } from '../types'
import { addMember } from './api'
import type { CrmMemberInput, CrmOrganisation } from './types'

type Mode = 'bestaand' | 'nieuw'

const MODES: { value: Mode; label: string }[] = [
  { value: 'bestaand', label: 'Bestaand contact' },
  { value: 'nieuw', label: 'Nieuw contact' },
]

const fieldClass = 'flex flex-col gap-1.5 text-sm font-medium'

/**
 * "+ Contactpersoon" (design §4.4): link an existing contact or create a new one, with
 * a role. A founder needs explicit confirmation (spec-59: founders are no relations);
 * an e-mail that already belongs to a contact switches to that contact.
 *
 * Escape or a click outside closes the dialog without throwing the input away (IA-15);
 * "Annuleren" and a successful link start the next time from scratch.
 */
export function AddMemberDialog({
  organisation,
  open,
  onOpenChange,
  onAdded,
}: {
  organisation: CrmOrganisation
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdded: () => void
}) {
  const [mode, setMode] = useState<Mode>('bestaand')
  const [query, setQuery] = useState('')
  // null = no answer for the current text (yet).
  const [results, setResults] = useState<CrmContactListItem[] | null>(null)
  const [searchFailed, setSearchFailed] = useState(false)
  const [selected, setSelected] = useState<{ person_id: string; full_name: string } | null>(null)
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('')
  const [primary, setPrimary] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [founderQuestion, setFounderQuestion] = useState<string | null>(null)
  const timer = useRef<number | undefined>(undefined)
  /** Numbers the searches: an answer that is not for the newest one is dropped. */
  const lookup = useRef(0)
  const submitting = useRef(false)
  /** The field the person types in first: the search box, or the name of a new contact. */
  const firstField = useRef<HTMLInputElement>(null)

  useEffect(() => () => window.clearTimeout(timer.current), [])

  function reset() {
    window.clearTimeout(timer.current)
    lookup.current += 1
    setMode('bestaand')
    setQuery('')
    setResults(null)
    setSearchFailed(false)
    setSelected(null)
    setFullName('')
    setEmail('')
    setPhone('')
    setRole('')
    setPrimary(false)
    setError(null)
    setFounderQuestion(null)
  }

  /** A change of person or input: an earlier error or founder question was about the old one. */
  function edit() {
    setError(null)
    setFounderQuestion(null)
  }

  function search(value: string) {
    setQuery(value)
    setSelected(null)
    edit()
    window.clearTimeout(timer.current)
    lookup.current += 1
    const mine = lookup.current
    setSearchFailed(false)
    if (value.trim().length < 2) {
      setResults(null)
      return
    }
    timer.current = window.setTimeout(() => {
      listContacts({ q: value.trim(), limit: 8 })
        .then((result) => {
          if (mine === lookup.current) setResults(result.items)
        })
        .catch(() => {
          if (mine === lookup.current) {
            setResults(null)
            setSearchFailed(true)
          }
        })
    }, 300)
  }

  async function submit(confirmFounder = false) {
    if (submitting.current) return
    const base = {
      role: role.trim() || null,
      is_primary: primary || undefined,
      confirm_founder: confirmFounder || undefined,
    }
    let input: CrmMemberInput
    if (mode === 'bestaand') {
      if (!selected) {
        setError('Kies een contact.')
        return
      }
      input = { ...base, person_id: selected.person_id }
    } else {
      if (!fullName.trim()) {
        setError('Naam is verplicht.')
        return
      }
      input = {
        ...base,
        new_contact: {
          full_name: fullName.trim(),
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
        },
      }
    }
    submitting.current = true
    setBusy(true)
    setError(null)
    try {
      const member = await addMember(organisation.id, input)
      toast.success(`${member.full_name} is gekoppeld aan ${organisation.name}.`)
      reset()
      onAdded()
      onOpenChange(false)
    } catch (caught) {
      if (caught instanceof CrmApiError && caught.code === 'founder_confirmation_required') {
        setFounderQuestion(caught.message)
      } else if (caught instanceof CrmApiError && caught.code === 'contact_exists') {
        const personId = caught.body?.person_id
        if (typeof personId === 'string') {
          setMode('bestaand')
          setSelected({ person_id: personId, full_name: fullName.trim() })
        }
        setError(caught.message)
      } else {
        setError(caught instanceof Error ? caught.message : 'Koppelen mislukt.')
      }
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault()
    void submit()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          // The first field, not the first radio button. Only when it is there: after a
          // contact was chosen the dialog has no field and keeps Radix's own choice.
          if (!firstField.current) return
          event.preventDefault()
          firstField.current.focus()
        }}
      >
        <DialogHeader>
          <DialogTitle>Contactpersoon toevoegen</DialogTitle>
          <DialogDescription>Aan {organisation.name}</DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          {/* Native radios: the arrow keys, the single tab stop and the group name come with them. */}
          <fieldset className="flex min-w-0 gap-2 border-0 p-0">
            <legend className="sr-only">Soort contact</legend>
            {MODES.map((item) => (
              <label
                key={item.value}
                className={cn(
                  'cursor-pointer rounded-full border px-3 py-1 text-sm has-focus-visible:ring-3 has-focus-visible:ring-ring/50',
                  mode === item.value ? 'border-primary bg-accent' : 'border-border',
                )}
              >
                <input
                  type="radio"
                  name="soort-contact"
                  className="sr-only"
                  checked={mode === item.value}
                  onChange={() => {
                    setMode(item.value)
                    edit()
                  }}
                />
                {item.label}
              </label>
            ))}
          </fieldset>
          {mode === 'bestaand' ? (
            <div className={fieldClass}>
              {selected ? (
                <>
                  <span>Contact</span>
                  <p className="flex items-center justify-between rounded-md border border-border px-3 py-2 font-normal">
                    {selected.full_name || 'Gekozen contact'}
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={() => {
                        setSelected(null)
                        edit()
                      }}
                    >
                      Wijzigen
                    </button>
                  </p>
                </>
              ) : (
                <>
                  <label htmlFor="lid-zoek">Contact</label>
                  <Input
                    ref={firstField}
                    id="lid-zoek"
                    value={query}
                    onChange={(event) => search(event.target.value)}
                    placeholder="Zoek op naam of e-mail…"
                  />
                  {results !== null && results.length > 0 ? (
                    <ul aria-label="Gevonden contacten" className="flex flex-col rounded-md border border-border">
                      {results.map((contact) => (
                        <li key={contact.person_id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelected({ person_id: contact.person_id, full_name: contact.full_name })
                              edit()
                            }}
                            className="w-full px-3 py-2 text-left font-normal hover:bg-muted"
                          >
                            {contact.full_name}
                            {contact.email ? (
                              <span className="text-muted-foreground"> · {contact.email}</span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {results !== null && results.length === 0 ? (
                    <p role="status" className="font-normal text-muted-foreground">
                      Geen contacten gevonden. Kies &lsquo;Nieuw contact&rsquo; om er een toe te voegen.
                    </p>
                  ) : null}
                  {searchFailed ? (
                    <p role="alert" className="font-normal text-destructive">
                      Zoeken mislukt. Probeer het opnieuw.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : (
            <>
              <label className={fieldClass}>
                Naam *
                <Input
                  ref={firstField}
                  value={fullName}
                  onChange={(event) => {
                    setFullName(event.target.value)
                    edit()
                  }}
                />
              </label>
              <label className={fieldClass}>
                E-mail
                <Input
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                    edit()
                  }}
                />
              </label>
              <label className={fieldClass}>
                Telefoon
                <Input
                  type="tel"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value)
                    edit()
                  }}
                />
              </label>
            </>
          )}
          <label className={fieldClass}>
            Rol bij {organisation.name}
            <Input
              value={role}
              onChange={(event) => setRole(event.target.value)}
              placeholder="Bijv. Programmamanager"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={primary}
              onChange={(event) => setPrimary(event.target.checked)}
            />
            Primaire organisatie van dit contact
          </label>
          {founderQuestion ? (
            <div role="alert" className="rounded-lg border border-warning-border bg-warning-subtle p-3 text-sm">
              <p>{founderQuestion}</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="mt-2"
                disabled={busy}
                onClick={() => void submit(true)}
              >
                Toch als relatie toevoegen
              </Button>
            </div>
          ) : null}
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset()
                onOpenChange(false)
              }}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Bezig…' : 'Koppelen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
